import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import SEO from '@/components/shared/SEO'

const CustomerReviewPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    rating: 0,
    review: '',
  })

  const [hoveredRating, setHoveredRating] = useState(0)
  const [showThankYou, setShowThankYou] = useState(false)

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleRatingClick = rating => {
    setFormData(prev => ({
      ...prev,
      rating,
    }))
  }

  const handleSubmit = async e => {
    e.preventDefault()

    try {
      // Prepare data for API submission
      const submissionData = {
        web_form_id: 'claims1_customer_review',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        rating: formData.rating,
        review: formData.review,
        submittedAt: new Date().toISOString(),
      }

      // Submit to TananTech API
      const response = await fetch(
        'https://datastore.tanantech.co.uk/1.0/data',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(submissionData),
        }
      )

      if (response.ok) {
        const result = await response.json()
        // eslint-disable-next-line no-console
        console.log('Review submitted successfully with ID:', result.dataid)

        // Show thank you popup
        setShowThankYou(true)

        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          rating: 0,
          review: '',
        })
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to submit review:', response.statusText)
        alert('There was an error submitting your review. Please try again.')
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting review:', error)
      alert('There was an error submitting your review. Please try again.')
    }
  }

  // Auto-close thank you popup after 20 seconds
  useEffect(() => {
    if (showThankYou) {
      const timer = setTimeout(() => {
        setShowThankYou(false)
      }, 20000) // 20 seconds

      return () => clearTimeout(timer)
    }
  }, [showThankYou])

  const renderStars = () => {
    return [...Array(5)].map((_, index) => {
      const starValue = index + 1
      return (
        <button
          key={index}
          type="button"
          className={`text-2xl transition-colors ${
            starValue <= (hoveredRating || formData.rating)
              ? 'text-green-500'
              : 'text-gray-300'
          }`}
          onClick={() => handleRatingClick(starValue)}
          onMouseEnter={() => setHoveredRating(starValue)}
          onMouseLeave={() => setHoveredRating(0)}
        >
          ★
        </button>
      )
    })
  }

  return (
    <>
      <SEO
        title="Share Your Experience - Customer Reviews | Claims1"
        description="Share your experience with Claims1 car finance claims service. Leave a review to help other customers and help us improve our service."
        keywords="Claims1 reviews, customer feedback, car finance claims reviews, customer experience, testimonials"
        url="/customer-review"
        image="/Image/Car-photo-illustration-claims1.webp"
        imageAlt="Claims1 customer reviews and feedback"
        schemaType="WebPage"
        schemaData={{
          mainEntity: {
            '@type': 'WebPage',
            name: 'Customer Review Form',
            description:
              'Submit your review and feedback about Claims1 car finance claims service',
            publisher: {
              '@type': 'Organization',
              name: 'Claims1',
              url: 'https://claims1.co.uk',
            },
          },
        }}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Customer Reviews', url: '/customer-review' },
        ]}
      />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="mb-12 text-center">
            <h1 className="mb-4 text-4xl font-black tracking-tight text-gray-900 uppercase md:text-5xl">
              SHARE YOUR EXPERIENCE
            </h1>
            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-gray-600">
              Help other customers by sharing your experience with Claims1.
              <br />
              Your feedback is valuable to us and helps us improve our service.
            </p>
          </div>

          {/* Main Content */}
          <div className="mx-auto max-w-6xl">
            <div className="overflow-hidden rounded-lg bg-white shadow-lg">
              <div className="grid md:grid-cols-2">
                {/* Left Side - Gradient Section */}
                <div
                  className="flex flex-col justify-center p-8 text-white md:p-12"
                  style={{
                    background:
                      'linear-gradient(to bottom right, oklch(0.2205 0.0376 194.77) 0%, oklch(0.6942 0.1185 194.77) 70%, oklch(0.769 0.188 70.08) 100%)',
                  }}
                >
                  <h2 className="mb-6 text-3xl font-bold md:text-4xl">
                    Share Your Feedback
                  </h2>
                  <p className="text-lg leading-relaxed text-white/90">
                    Your review helps other customers understand our service and
                    helps us continue to improve. Share your experience with
                    Claims1.
                  </p>
                </div>

                {/* Right Side - Form */}
                <div className="p-8 md:p-12">
                  <h3 className="mb-8 text-2xl font-bold text-gray-900">
                    Leave a Review
                  </h3>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name Fields */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label
                          htmlFor="firstName"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          First Name*
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="w-full rounded-md border border-gray-300 px-4 py-3 transition-colors focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="lastName"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Last Name*
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="w-full rounded-md border border-gray-300 px-4 py-3 transition-colors focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Email Field */}
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Email Address*
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full rounded-md border border-gray-300 px-4 py-3 transition-colors focus:border-transparent focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Rating Field */}
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Rating*
                      </label>
                      <div className="mb-2 flex items-center gap-1">
                        {renderStars()}
                      </div>
                      <p className="text-sm text-red-500">Rating required</p>
                    </div>

                    {/* Review Field */}
                    <div>
                      <label
                        htmlFor="review"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Your Review*
                      </label>
                      <textarea
                        id="review"
                        name="review"
                        value={formData.review}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        maxLength={500}
                        placeholder="Tell us about your experience with Claims1..."
                        className="resize-vertical w-full rounded-md border border-gray-300 px-4 py-3 transition-colors focus:border-transparent focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="mt-1 flex items-center justify-between">
                        <div></div>
                        <p className="text-sm text-gray-500">
                          {formData.review.length}/500 characters maximum
                        </p>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4 text-center">
                      <Button
                        type="submit"
                        className="h-14 px-8 text-xl font-black"
                      >
                        Submit Review
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Thank You Popup */}
        {showThankYou && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="mx-4 max-w-md rounded-lg bg-white p-8 text-center shadow-2xl">
              <div className="mb-4">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <svg
                    className="h-8 w-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                </div>
                <h3 className="mb-2 text-2xl font-bold text-gray-900">
                  Thank You!
                </h3>
                <p className="mb-6 text-gray-600">
                  Your review has been submitted successfully. We appreciate
                  your feedback and it helps us improve our service.
                </p>
                <Button
                  onClick={() => setShowThankYou(false)}
                  className="h-14 px-8 text-xl font-black"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default CustomerReviewPage
